import { z } from "zod";
import {
  count,
  finite,
  hash,
  id,
  jsonObjectSchema,
  projectIdSchema,
  gameVersionSchema,
  snapshotDataSchema,
} from "./scene.ts";
export {
  projectIdSchema,
  snapshotDataSchema,
  jsonObjectSchema,
} from "./scene.ts";
export type {
  SnapshotData,
  SceneNode,
  JsonObject,
  JsonValue,
} from "./scene.ts";
export const LIMITS = {
  batchBytes: 2 * 1024 * 1024,
  recordBytes: 512 * 1024,
  batchRecords: 100,
  objects: 512,
  sessionRecords: 100_000,
  sessionBytes: 256 * 1024 * 1024,
  receiveMs: 86400000,
  requestMs: 5000,
  tuningFields: 128,
} as const;
const scopeSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("session") }),
  z.strictObject({
    type: z.literal("attempt"),
    attemptId: z.uuid(),
    unitId: id,
    startSessionMs: count,
  }),
]);
const link = { snapshotRecordId: z.uuid().optional() };
const metric = z.strictObject({
  key: id,
  label: id,
  value: finite,
  unit: id.optional(),
});
const system = z.discriminatedUnion("name", [
  z.strictObject({
    type: z.literal("system"),
    name: z.literal("session_started"),
    payload: z.strictObject({
      paused: z.boolean(),
      visibility: z.enum(["visible", "hidden"]),
    }),
    ...link,
  }),
  z.strictObject({
    type: z.literal("system"),
    name: z.literal("visibility_changed"),
    payload: z.strictObject({ state: z.enum(["visible", "hidden"]) }),
    ...link,
  }),
  z.strictObject({
    type: z.literal("system"),
    name: z.literal("attempt_started"),
    payload: z.strictObject({
      reason: z.enum(["initial", "retry", "next"]),
      previousAttemptId: z.uuid().optional(),
      context: jsonObjectSchema,
    }),
    ...link,
  }),
  z
    .strictObject({
      type: z.literal("system"),
      name: z.literal("attempt_ended"),
      payload: z.strictObject({
        completion: z.enum(["finished", "aborted"]),
        result: z.enum(["success", "failure", "draw", "none"]),
        reason: id,
        metrics: z
          .array(metric)
          .max(32)
          .refine((ms) => new Set(ms.map((m) => m.key)).size === ms.length),
      }),
      ...link,
    })
    .refine(
      (e) => e.payload.completion !== "aborted" || e.payload.result === "none",
    ),
  z.strictObject({
    type: z.literal("system"),
    name: z.literal("paused"),
    payload: z.strictObject({}),
    ...link,
  }),
  z.strictObject({
    type: z.literal("system"),
    name: z.literal("resumed"),
    payload: z.strictObject({}),
    ...link,
  }),
  z.strictObject({
    type: z.literal("system"),
    name: z.literal("feedback_submitted"),
    payload: z.strictObject({ questionId: id, rating: z.number().int().min(1).max(5) }),
  }),
  z.strictObject({
    type: z.literal("system"),
    name: z.literal("recording_closed"),
    payload: z.strictObject({
      mode: z.enum(["normal", "truncated"]),
      finalSeq: count.positive(),
      droppedRanges: z
        .array(z.tuple([count.positive(), count.positive()]))
        .max(256),
    }),
  }),
]);
export const eventDataSchema = z.union([
  system,
  z.strictObject({
    type: z.literal("game"),
    name: z
      .string()
      .max(128)
      .regex(/^[a-z][a-z0-9_-]*:[a-z][a-z0-9_-]*$/),
    label: id,
    category: z.enum(["input", "fact", "diagnostic"]),
    entities: z
      .array(id)
      .max(64)
      .refine((ids) => new Set(ids).size === ids.length),
    payload: jsonObjectSchema,
    ...link,
  }),
]);
export type EventData = z.infer<typeof eventDataSchema>;
const dimension = finite.nonnegative().max(1e6),
  size = z.strictObject({ width: dimension, height: dimension });
export const environmentDataSchema = z.strictObject({
  platform: z.enum(["desktop_web", "mobile_web", "unknown"]),
  os: z.strictObject({ name: id.nullable(), version: id.nullable() }),
  browser: z.strictObject({ name: id.nullable(), version: id.nullable() }),
  screenCss: size.nullable(),
  viewportCss: size.nullable(),
  gameRectCss: size.extend({ x: finite, y: finite }).nullable(),
  devicePixelRatio: finite.positive().nullable(),
  canvasPixels: z
    .strictObject({ width: count.max(1e6), height: count.max(1e6) })
    .nullable(),
  inputMethods: z
    .array(z.enum(["mouse", "touch", "keyboard", "pen"]))
    .max(4)
    .refine((a) => new Set(a).size === a.length),
});
export type EnvironmentData = z.infer<typeof environmentDataSchema>;
const common = {
  recordId: z.uuid(),
  seq: count.positive(),
  sessionMs: count,
  scope: scopeSchema,
};
export const recordSchema = z
  .discriminatedUnion("kind", [
    z.strictObject({
      ...common,
      kind: z.literal("event"),
      data: eventDataSchema,
    }),
    z.strictObject({
      ...common,
      kind: z.literal("snapshot"),
      data: snapshotDataSchema,
    }),
    z.strictObject({
      ...common,
      kind: z.literal("environment"),
      data: environmentDataSchema,
    }),
  ])
  .superRefine((r, c) => {
    const bad = (message: string) => c.addIssue({ code: "custom", message });
    if (r.scope.type === "attempt" && r.scope.startSessionMs > r.sessionMs)
      bad("Attempt starts after record");
    if (r.kind === "environment" && r.scope.type !== "session")
      bad("Environment scope");
    if (r.kind !== "event" || r.data.type !== "system") return;
    const e = r.data;
    const attempt = e.name === "attempt_started" || e.name === "attempt_ended";
    if (attempt !== (r.scope.type === "attempt")) bad("System event scope");
    if (
      e.name === "attempt_started" &&
      r.scope.type === "attempt" &&
      r.sessionMs !== r.scope.startSessionMs
    )
      bad("Start offset");
    if (e.name === "session_started" && r.sessionMs !== 0)
      bad("Session starts at zero");
    if (e.name === "recording_closed") {
      if (e.payload.finalSeq !== r.seq) bad("Closure sequence");
      let previous = 0;
      for (const [a, b] of e.payload.droppedRanges) {
        if (a <= previous || b < a || b >= r.seq) bad("Dropped ranges");
        previous = b;
      }
    }
  });
export type PlayRecord = z.infer<typeof recordSchema>;
export const presentationRefSchema = z.strictObject({
  sceneVersion: z.literal(1),
  manifestSha256: hash,
});
export const metadataSchema = z.strictObject({
  protocolVersion: z.literal(3),
  projectId: projectIdSchema,
  sessionId: z.uuid(),
  gameVersion: gameVersionSchema,
  sessionStartedAt: z.iso.datetime(),
  capabilities: z.strictObject({ snapshots: z.boolean() }),
  presentation: presentationRefSchema.nullable(),
});
export const batchSchema = metadataSchema
  .extend({ records: z.array(recordSchema).min(1).max(LIMITS.batchRecords) })
  .superRefine((b, c) => {
    const bad = (message: string) => c.addIssue({ code: "custom", message });
    if (b.capabilities.snapshots !== (b.presentation !== null))
      bad("Presentation capability");
    if (
      !b.capabilities.snapshots &&
      b.records.some((r) => r.kind === "snapshot")
    )
      bad("Snapshots disabled");
    if (
      new Set(b.records.map((r) => r.recordId)).size !== b.records.length ||
      new Set(b.records.map((r) => r.seq)).size !== b.records.length
    )
      bad("Duplicate record");
    const sorted = [...b.records].sort((a, b) => a.seq - b.seq);
    for (let i = 1; i < sorted.length; i++)
      if (sorted[i].sessionMs < sorted[i - 1].sessionMs)
        bad("Nonmonotonic time");
  });
export type PlayBatch = z.infer<typeof batchSchema>;
export type SessionMetadata = z.infer<typeof metadataSchema>;
// Game-local parameter attribution uses this value shape, never a required wire field.
export interface TuningState {
  baseFingerprint: string;
  overlay: Record<string, number>;
}
export const acknowledgmentSchema = z.strictObject({
  requestId: z.string().min(1),
  acknowledgedRecordIds: z.array(z.uuid()).max(LIMITS.batchRecords),
});
export const jsonBytes = (value: unknown): number =>
  new TextEncoder().encode(JSON.stringify(value)).byteLength;

/** Stable field ordering for identity comparison, never used as a shell command. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value !== null && typeof value === "object")
    return `{${Object.entries(value)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`)
      .join(",")}}`;
  return JSON.stringify(value);
}

/** 非加密指纹：只要求同值同串、异值高概率异串。两条独立的 FNV-1a 32 位链拼成 16 位十六进制。 */
export function fingerprint(value: unknown): string {
  const serialized = canonicalJson(value);
  let high = 0x811c9dc5;
  let low = 0x9dc5811c;
  for (let i = 0; i < serialized.length; i += 1) {
    const code = serialized.charCodeAt(i);
    high = Math.imul(high ^ code, 0x01000193) >>> 0;
    low = Math.imul(low ^ (code + i), 0x01000193) >>> 0;
  }
  return high.toString(16).padStart(8, "0") + low.toString(16).padStart(8, "0");
}
